import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const EMAIL = 'contato@bibliotecadebolso.app';

const content = {
  en: {
    title: 'Support',
    subtitle: 'Biblioteca de Bolso — Help Center',
    back: 'Back',
    contact: {
      heading: 'Contact Us',
      body: 'Have a question or need help? Send us an email and we\'ll get back to you within 2 business days.',
      cta: 'Send an email',
    },
    faq: {
      heading: 'Frequently Asked Questions',
      items: [
        {
          q: 'How do I add a book to my collection?',
          a: 'Tap the add button on the Collection screen. You can search by title or enter the book details manually.',
        },
        {
          q: 'How do I request a loan?',
          a: 'Find the book in the Public Library or on a friend\'s profile and tap "Request loan".',
        },
        {
          q: 'How does the Premium plan work?',
          a: 'Premium is a one-time purchase that unlocks unlimited collection size, unlimited clubs, and no daily limits on Explore.',
        },
        {
          q: 'I forgot my password. What should I do?',
          a: 'On the login screen, tap "Forgot my password" and enter your email to receive a reset link.',
        },
        {
          q: 'How do I delete my account?',
          a: 'Send us an email at the address below with your account email. We process the request within 5 business days.',
        },
      ],
    },
    technical: {
      heading: 'Technical Issues',
      body: 'If you encounter a bug or unexpected behavior, email us describing the issue. Screenshots are welcome.',
    },
    footer: 'Biblioteca de Bolso — bibliotecadebolso.app',
  },
  pt: {
    title: 'Suporte',
    subtitle: 'Biblioteca de Bolso — Central de ajuda',
    back: 'Voltar',
    contact: {
      heading: 'Fale conosco',
      body: 'Tem alguma duvida ou precisa de ajuda? Mande um e-mail e respondemos em ate 2 dias uteis.',
      cta: 'Enviar e-mail',
    },
    faq: {
      heading: 'Perguntas frequentes',
      items: [
        {
          q: 'Como adicionar um livro ao meu acervo?',
          a: 'Toque no botao de adicionar na tela Acervo. Voce pode buscar pelo titulo ou cadastrar manualmente.',
        },
        {
          q: 'Como solicitar um emprestimo?',
          a: 'Encontre o livro na Biblioteca Publica ou no perfil de um amigo e toque em "Solicitar emprestimo".',
        },
        {
          q: 'Como funciona o plano Premium?',
          a: 'O Premium e um pagamento unico que desbloqueia acervo ilimitado, clubes ilimitados e sem limites diarios no Explorar.',
        },
        {
          q: 'Esqueci minha senha. O que fazer?',
          a: 'Na tela de login, toque em "Esqueci minha senha" e insira seu e-mail para receber o link de redefinicao.',
        },
        {
          q: 'Como excluir minha conta?',
          a: 'Envie um e-mail para o endereco abaixo informando o e-mail da sua conta. Processamos em ate 5 dias uteis.',
        },
      ],
    },
    technical: {
      heading: 'Problemas tecnicos',
      body: 'Se encontrar um erro ou comportamento inesperado no app, envie um e-mail descrevendo o problema. Capturas de tela sao bem-vindas.',
    },
    footer: 'Biblioteca de Bolso — bibliotecadebolso.app',
  },
  es: {
    title: 'Soporte',
    subtitle: 'Biblioteca de Bolso — Centro de ayuda',
    back: 'Volver',
    contact: {
      heading: 'Contactanos',
      body: 'Tienes alguna pregunta o necesitas ayuda? Envianos un correo electronico y te responderemos en un plazo de 2 dias habiles.',
      cta: 'Enviar correo',
    },
    faq: {
      heading: 'Preguntas frecuentes',
      items: [
        {
          q: 'Como agrego un libro a mi coleccion?',
          a: 'Toca el boton de agregar en la pantalla de Coleccion. Puedes buscar por titulo o ingresar los datos manualmente.',
        },
        {
          q: 'Como solicito un prestamo?',
          a: 'Encuentra el libro en la Biblioteca Publica o en el perfil de un amigo y toca "Solicitar prestamo".',
        },
        {
          q: 'Como funciona el plan Premium?',
          a: 'Premium es un pago unico que desbloquea coleccion ilimitada, clubes ilimitados y sin limites diarios en Explorar.',
        },
        {
          q: 'Olvide mi contrasena. Que debo hacer?',
          a: 'En la pantalla de inicio de sesion, toca "Olvide mi contrasena" e ingresa tu correo para recibir el enlace de restablecimiento.',
        },
        {
          q: 'Como elimino mi cuenta?',
          a: 'Envianos un correo con el email de tu cuenta. Procesamos la solicitud en un plazo de 5 dias habiles.',
        },
      ],
    },
    technical: {
      heading: 'Problemas tecnicos',
      body: 'Si encuentras un error o un comportamiento inesperado en la app, envianos un correo describiendo el problema. Las capturas de pantalla son bienvenidas.',
    },
    footer: 'Biblioteca de Bolso — bibliotecadebolso.app',
  },
};

function detectLang() {
  const lang = (navigator.language || 'pt').toLowerCase();
  if (lang.startsWith('pt')) return 'pt';
  if (lang.startsWith('es')) return 'es';
  return 'en';
}

function Secao({ titulo, children }) {
  return (
    <section style={{ marginBottom: 36 }}>
      <h2 style={{ fontSize: 18, fontWeight: 800, color: '#211C2B', margin: '0 0 12px', lineHeight: 1.3 }}>
        {titulo}
      </h2>
      <div style={{ fontSize: 15, color: '#4A4458', lineHeight: 1.75 }}>
        {children}
      </div>
    </section>
  );
}

const LANGS = ['en', 'pt', 'es'];

export default function Suporte() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(detectLang);
  const t = content[lang];

  return (
    <div style={{ minHeight: '100dvh', background: '#FAF8F5', fontFamily: 'inherit' }}>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 64px' }}>
        <div style={{ paddingTop: 48, paddingBottom: 32, borderBottom: '1px solid #ECE7E1', marginBottom: 40 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5B3CE4', fontWeight: 600, fontSize: 14, padding: 0 }}
            >
              {t.back}
            </button>
            <div style={{ display: 'flex', gap: 4 }}>
              {LANGS.map(l => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  style={{
                    background: lang === l ? '#5B3CE4' : 'none',
                    border: '1px solid',
                    borderColor: lang === l ? '#5B3CE4' : '#D4CDE8',
                    borderRadius: 20,
                    cursor: 'pointer',
                    color: lang === l ? '#fff' : '#5B3CE4',
                    fontWeight: 600,
                    fontSize: 12,
                    padding: '4px 10px',
                    textTransform: 'uppercase',
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <h1 style={{ fontSize: 30, fontWeight: 900, color: '#211C2B', margin: 0, letterSpacing: -0.5 }}>
            {t.title}
          </h1>
          <p style={{ fontSize: 16, color: '#6E6878', margin: '8px 0 0', fontWeight: 500 }}>
            {t.subtitle}
          </p>
        </div>

        <Secao titulo={t.contact.heading}>
          <p style={{ marginTop: 0 }}>{t.contact.body}</p>
          <a
            href={`mailto:${EMAIL}`}
            style={{
              display: 'inline-block',
              background: '#5B3CE4',
              color: '#fff',
              fontWeight: 700,
              fontSize: 15,
              padding: '12px 24px',
              borderRadius: 12,
              textDecoration: 'none',
              marginTop: 4,
            }}
          >
            {t.contact.cta}
          </a>
          <p style={{ marginTop: 12, marginBottom: 0, color: '#6E6878', fontSize: 14 }}>
            {EMAIL}
          </p>
        </Secao>

        <Secao titulo={t.faq.heading}>
          {t.faq.items.map((item, i) => (
            <div key={i} style={{ marginBottom: i < t.faq.items.length - 1 ? 16 : 0 }}>
              <p style={{ fontWeight: 700, margin: '0 0 4px' }}>{item.q}</p>
              <p style={{ margin: 0 }}>{item.a}</p>
            </div>
          ))}
        </Secao>

        <Secao titulo={t.technical.heading}>
          <p style={{ marginTop: 0 }}>{t.technical.body}</p>
          <a
            href={`mailto:${EMAIL}`}
            style={{ color: '#5B3CE4', fontWeight: 600, textDecoration: 'none' }}
          >
            {EMAIL}
          </a>
        </Secao>

        <div style={{ borderTop: '1px solid #ECE7E1', paddingTop: 24, color: '#9A93A4', fontSize: 13 }}>
          {t.footer}
        </div>
      </div>
    </div>
  );
}
